/** The page's address. Absolute: inside its splat route a relative link resolves against the whole URL. */
export const PAGE_URL = '/institute/generate-vouchers'

export type RunType = 'all' | 'classwise' | 'individual'
export type AdvanceMode = 'withoutadvancefee' | 'withadvancefee' | 'withindividualadvancevouchers'
export type GateChoice = 'convert' | 'skip'

/** One voucher per chosen month, for individual students. */
export const INDIVIDUAL_ADVANCE: AdvanceMode = 'withindividualadvancevouchers'

export type Option = {label: string; value: number | string}

export type EditParticulars = {heads: number[]; discount: boolean; previousBalance: boolean}

/** GET /generate-vouchers/options */
export type GenerateOptions = {
  months: string[]
  classes: {id: number; name: string}[]
  labels: {id: number; name: string}[]
  reminderDays: {before: number | null; after: number | null}
  lastRunAt: string
  currentMonthGenerated: boolean
  editParticulars: {offered: boolean; heads: {id: number; name: string; amount: number}[]}
}

/** The request the preview and a new run share. */
export type RunRequest = {
  month: string
  type: RunType
  classIds?: number[]
  studentIds?: number[]
  advance: AdvanceMode
  advanceMonths: string[]
  reminder: {before?: string; after?: string}
  editParticulars: EditParticulars | null
  gate?: GateChoice
}

export type GateVoucher = {
  fee_collection_id: number
  user_id: number
  student_name: string
  studentno: string
  class_name: string
  month: string
  year: string
}

export type StoreItems = {
  missingCostOfSale: string[]
  missingInventory: string[]
  insufficientQuantity: string[]
}

/** What a run does to a student in a month: the preview's counts, by name. */
export type Fate = 'new' | 'rewrite' | 'paid' | 'laterMonth'

/** Each fate's text id under FEE.GENERATE_VOUCHERS.COUNTS. */
export const FATE_COUNT: Record<Fate, string> = {
  new: 'NEW',
  rewrite: 'REWRITE',
  paid: 'PAID',
  laterMonth: 'LATER_MONTH',
}

/** POST /generate-vouchers/preview/students: a student behind a count, in the month it is for. */
export type PreviewStudent = {
  user_student_id: number
  studentno: string | null
  student_name: string
  class_name: string | null
  month: string
  fate: Fate
}

export type MonthCounts = {
  month: string
  laterMonth: number
  paid: number
  rewrite: number
  new: number
}

/** POST /generate-vouchers/preview */
export type Preview = {
  month: string
  months: string[]
  type: RunType
  counts: {students: number; laterMonth: number; paid: number; rewrite: number; new: number}
  /** Individual Advance Vouchers: what happens to the students in each month. */
  byMonth: MonthCounts[] | null
  classes: {id: number; name: string; students: number}[] | null
  chunks: number
  gate: {blocking: GateVoucher[]; convertible: GateVoucher[]}
  checks: {feeStructureMissing: boolean; storeItems: StoreItems; withoutParentingId: number}
}

export type PartStatus = 'pending' | 'running' | 'done' | 'failed' | 'unknown' | 'gate' | 'refused'
export type RunState = 'running' | 'interrupted' | 'completed' | 'closed'

export type RunPart = {
  index: number
  classId?: number
  className?: string | null
  /** Individual Advance Vouchers: the month this part generates. */
  month?: string
  /** Sent parts: the students sent; the others: the class as it stands now. */
  students?: number
  status: PartStatus
  // Left out where zero.
  made?: number
  rewritten?: number
  untouched?: number
  laterMonth?: number
  /** Seconds PHP took, when known. */
  secs?: number
}

export type RunTotals = {
  chunks: number
  made: number
  rewritten: number
  untouched: number
  laterMonth: number
} & Partial<Record<PartStatus, number>>

/** A run as node-api describes it. */
export type Run = {
  id: number
  startedBy: string | null
  /** Whether the signed-in user started the run: only they can send its parts. */
  mine: boolean
  startedAt: string
  finishedAt: string | null
  month: string
  /** Individual Advance Vouchers: every month the run generates. */
  months?: string[]
  type: RunType
  advance: AdvanceMode
  editParticulars: boolean
  state: RunState
  totals: RunTotals
  chunks: RunPart[]
  notGenerated?: {
    user_student_id: number
    studentno: string
    student_name: string
    class_name: string | null
    latest_month: string
  }[]
}

/** How a run's month reads: every month for Individual Advance Vouchers, else its one month. */
export const runMonths = (run: {month: string; months?: string[]}) =>
  (run.months ?? [run.month]).map((month) => month.replace('-', ' ')).join(', ')

/** A run's kind, by its page text id under FEE.GENERATE_VOUCHERS: Individual Advance Vouchers, or its type. */
export const runKind = (
  run: {type: RunType; advance?: AdvanceMode},
  text: (id: string) => string
) =>
  text(run.advance === INDIVIDUAL_ADVANCE ? 'INDIVIDUAL_ADVANCE' : `TYPE.${run.type.toUpperCase()}`)

/** A part as the page names it: its class, or its students, after its month for Individual Advance Vouchers. */
export const partName = (
  part: RunPart,
  text: (id: string, values?: Record<string, any>) => string
) =>
  `${part.month ? `${part.month.replace('-', ' ')}: ` : ''}${
    part.classId === undefined
      ? text('RUN.STUDENTS_N', {count: part.students ?? 0})
      : (part.className ?? '')
  }`

/** How far a run is in students, and its generating time. */
export type Progress = {
  students: number
  done: number
  percent: number
  /** Seconds PHP took over the parts whose time is known. */
  spentSecs: number
  /** Seconds a student takes over the timed parts that went in; null before one with students did. */
  pace: number | null
  /** Students in the parts still to send. */
  waiting: number
  /** Students in the part being generated. */
  running: number
}

/**
 * @param queued the parts a pass still sends after the one in flight; without it, the pending ones
 */
export const runProgress = (run: Run, queued: Set<number> | null = null): Progress => {
  let students = 0
  let done = 0
  let waiting = 0
  let running = 0
  let spentSecs = 0
  let timedStudents = 0
  let timedSecs = 0
  for (const part of run.chunks) {
    const count = part.students ?? 0
    students += count
    spentSecs += part.secs ?? 0
    if (part.status === 'done') {
      done += count
      if (part.secs !== undefined && count > 0) {
        timedStudents += count
        timedSecs += part.secs
      }
    } else if (part.status === 'running') {
      running += count
    } else if (queued ? queued.has(part.index) : part.status === 'pending') {
      waiting += count
    }
  }
  return {
    students,
    done,
    percent: students ? Math.floor((done * 100) / students) : 100,
    spentSecs,
    pace: timedStudents ? timedSecs / timedStudents : null,
    waiting,
    running,
  }
}

/**
 * What the parts still to go should take at the pace so far, or null with no pace or nothing to go.
 * Time already spent on the part in flight comes out of that part's share only.
 */
export const secsLeft = (progress: Progress, outSecs = 0): number | null =>
  progress.pace === null || progress.waiting + progress.running === 0
    ? null
    : progress.pace * progress.waiting + Math.max(0, progress.pace * progress.running - outSecs)

/** The floating notice's state for a run, or null where it has nothing to say. */
export type NoticePhase = 'GENERATING' | 'PAUSED' | 'STOPPED' | 'DONE'

export const noticePhase = (run: Run, driving: boolean): NoticePhase | null => {
  if (driving) return 'GENERATING'
  if (run.state === 'completed') return 'DONE'
  if (run.state === 'closed') return null
  return run.chunks.some((part) => part.status === 'pending') ? 'PAUSED' : 'STOPPED'
}
